import React, { useState, useEffect, useRef, useCallback } from 'react';
import './VoiceInput.css';

const VoiceInput = ({ onTextChange, onSendText, placeholder = "Describe tu viaje o haz una pregunta...", clearInputRef, onInputClick, onHelpClick }) => {
  const [text, setText] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [error, setError] = useState('');
  const recognitionRef = useRef(null);

  // Debug: Detectar re-renderizados
  console.log('🔄 VoiceInput re-renderizado, texto actual:', text);

  // Debug: Detectar montaje del componente
  useEffect(() => {
    console.log('🏠 VoiceInput montado por primera vez');
    return () => {
      console.log('🗑️ VoiceInput desmontado - esto causaría pérdida de texto');
    };
  }, []);

  useEffect(() => {
    // Verificar compatibilidad con Web Speech API
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (SpeechRecognition) {
      setIsSupported(true);
      
      // Configurar reconocimiento de voz
      recognitionRef.current = new SpeechRecognition();
      const recognition = recognitionRef.current;
      
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = 'es-ES'; // Español
      recognition.maxAlternatives = 1;

      recognition.onstart = () => {
        setIsListening(true);
        setError('');
      };

      recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setText(transcript);
        if (onTextChange) {
          onTextChange(transcript);
        }
        setIsListening(false);
      };

      recognition.onerror = (event) => {
        // Solo mostrar error si no es 'aborted' (que es normal cuando el usuario para manualmente)
        if (event.error !== 'aborted') {
          setError(`Error de reconocimiento: ${event.error}`);
        }
        setIsListening(false);
      };

      recognition.onend = () => {
        setIsListening(false);
        // Limpiar error después de un tiempo
        setTimeout(() => setError(''), 3000);
      };
    } else {
      setIsSupported(false);
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, [onTextChange]);

  const startListening = () => {
    if (recognitionRef.current && !isListening) {
      try {
        recognitionRef.current.start();
      } catch (error) {
        setError('Error al iniciar el reconocimiento de voz');
      }
    }
  };

  const stopListening = () => {
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
    }
  };

  const handleTextChange = (e) => {
    const newText = e.target.value;
    setText(newText);
    if (onTextChange) {
      onTextChange(newText);
    }
  };

  const clearText = useCallback(() => {
    console.log('🔍 clearText() llamado - esto NO debería pasar al enviar');
    setText('');
    if (onTextChange) {
      onTextChange('');
    }
  }, [onTextChange]);

  // Exponer la función clearText a través del ref
  useEffect(() => {
    if (clearInputRef) {
      clearInputRef.current = clearText;
    }
  }, [clearInputRef, clearText]);

  return (
    <div className="voice-input-container">
      <div className="voice-input-wrapper">
        <div className="voice-input-field">
          <textarea
            value={text}
            onChange={handleTextChange}
            onClick={onInputClick}
            placeholder="💬 Cuéntanos sobre tu viaje - Escribe o habla para describir tu viaje..."
            className="voice-textarea"
            rows={1}
            onInput={(e) => {
              // Auto-resize textarea
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
            }}
          />
          
          <div className="voice-controls">
            {isSupported && (
              <button
                onClick={isListening ? stopListening : startListening}
                className={`voice-button ${isListening ? 'listening' : ''}`}
                disabled={isListening}
                title={isListening ? 'Escuchando...' : 'Hablar'}
              >
                {isListening ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="12" cy="12" r="6"/>
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                    <line x1="12" y1="19" x2="12" y2="23"/>
                    <line x1="8" y1="23" x2="16" y2="23"/>
                  </svg>
                )}
              </button>
            )}
            
            {text && (
              <>
                <button
                  onClick={clearText}
                  className="clear-button"
                  title="Limpiar texto"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
                <button
                  onClick={() => {
                    console.log('📤 Botón enviar clickeado, texto actual:', text);
                    if (onSendText && text.trim()) {
                      console.log('✅ Enviando texto al componente padre:', text.trim());
                      onSendText(text.trim());
                      console.log('📝 Texto después de enviar (debería mantenerse):', text);
                      // No limpiar automáticamente - el componente padre decidirá cuándo limpiar
                    }
                  }}
                  className="send-button"
                  title="Enviar"
                  disabled={!text.trim()}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                  </svg>
                </button>
              </>
            )}
            
            {/* Icono de ayuda - siempre visible */}
            {onHelpClick && (
              <button
                onClick={onHelpClick}
                className="help-button"
                title="Guía de cotización"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#6b7280',
                  transition: 'all 0.2s ease',
                  marginLeft: '4px'
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = '#f3f4f6';
                  e.target.style.color = '#374151';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#6b7280';
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                  <point cx="12" cy="17" r="1"/>
                </svg>
              </button>
            )}
          </div>
        </div>
        
        {!isSupported && (
          <div className="voice-not-supported">
            🚫 Voz no disponible
          </div>
        )}
      </div>

      {error && (
        <div className="voice-error">
          ⚠️ {error}
        </div>
      )}
    </div>
  );
};

export default VoiceInput;
